import api from 'store/actions/api';

// ------------------------------------------------------------------
// Constants
// ------------------------------------------------------------------
const LLM_CONFIG_PAGE_NAME = '@llm_cid_detection_config';
const LLM_CHANNEL = 'LLM';
const NON_SEO_ZEROS = '0_0_0_0_0';
const STRATEGY_ORDER = ['utmSource', 'referer', 'userAgent'];

const SIGNAL_TO_QUERY_KEY: Record<string, string> = {
  utmSource: 'utm_source',
  referer: 'referer',
  userAgent: 'user_agent',
};

const CID_LOCALE_MAPPING: Record<string, string> = {
  'FR-BE': 'B2',
  'NL-BE': 'B1',
  'DE-AT': 'AT',
  'DA': 'DK',
  'SV-SE': 'SE',
};

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------
interface LlmPlatformEntry {
  queries: Record<string, string>;
  id: number;
}

interface LlmConfig {
  [platform: string]: LlmPlatformEntry;
}

interface LlmDetectionResult {
  id: number;
  name: string;
}

interface RequestSignals {
  utmSource?: string | null;
  referer?: string | null;
  userAgent?: string | null;
}

// ------------------------------------------------------------------
// Class
// ------------------------------------------------------------------
export class LlmCidResolver {
  private config: LlmConfig | null = null;

  constructor(private locale: string) {}

  static create(locale: string): LlmCidResolver {
    return new LlmCidResolver(locale);
  }

  loadConfig() {
    // A generator function binds its own `this`, so the instance has to be
    // captured here rather than relying on `this` inside the generator.
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    return function* (): Generator<unknown, void, unknown> {
      if (!__SERVER__) {
        return;
      }
      try {
        const response = (yield api.getMarketingCmsPage(
          LLM_CONFIG_PAGE_NAME,
        )) as { result?: { content?: string } };
        const content = response?.result?.content;
        const parsed = content ? JSON.parse(content) : null;
        const json = parsed?.json ? JSON.parse(parsed.json) : null;
        self.config = self.isValidConfig(json) ? json : null;
      } catch (_err) {
        self.config = null;
      }
    };
  }

  resolve(signals: RequestSignals, seoCid: string | null): string | null {
    if (!this.config) {
      return null;
    }
    const platform = this.detectPlatform(signals);

    if (!platform) {
      return null;
    }

    return this.buildCid(seoCid, platform.id);
  }

  private isValidConfig(config: unknown): config is LlmConfig {
    if (!config || typeof config !== 'object' || Array.isArray(config)) {
      return false;
    }

    return Object.values(config as Record<string, unknown>).every(
      (entry) =>
        entry !== null &&
        typeof entry === 'object' &&
        typeof (entry as LlmPlatformEntry).id === 'number' &&
        (entry as LlmPlatformEntry).queries !== null &&
        typeof (entry as LlmPlatformEntry).queries === 'object',
    );
  }

  private detectPlatform(signals: RequestSignals): LlmDetectionResult | null {
    const platforms = Object.entries(this.config ?? {});

    function matchPlatform(
      strategy: string,
      signalValue: string,
    ): [string, LlmPlatformEntry] | undefined {
      return platforms.find(([, platform]) => {
        const matchString = platform.queries[SIGNAL_TO_QUERY_KEY[strategy]];

        return (
          Boolean(matchString) &&
          signalValue.toLowerCase().includes(matchString.toLowerCase())
        );
      });
    }

    return STRATEGY_ORDER.reduce<LlmDetectionResult | null>(
      (result, strategy) => {
        if (result) {
          return result;
        }
        const rawValue = signals[strategy as keyof RequestSignals];

        if (!rawValue) {
          return null;
        }
        const signalValue =
          strategy === 'referer' ? this.extractHostname(rawValue) : rawValue;

        if (!signalValue) {
          return null;
        }
        const match = matchPlatform(strategy, signalValue);

        return match ? { id: match[1].id, name: match[0] } : null;
      },
      null,
    );
  }

  private buildCid(seoCid: string | null, platformId: number): string {
    if (seoCid) {
      const parts = seoCid.split('_');

      parts[1] = LLM_CHANNEL;
      parts[parts.length - 1] = String(platformId);

      return parts.join('_');
    }

    const prefix = this.getCIDPrefixFromLocale();

    return `${prefix}_${LLM_CHANNEL}_${NON_SEO_ZEROS}_${platformId}`;
  }

  private extractHostname(url: string): string | undefined {
    try {
      return new URL(url).hostname;
    } catch (_err) {
      return url.split('?')[0] || undefined;
    }
  }

  private getCIDPrefixFromLocale(): string {
    return (
      CID_LOCALE_MAPPING[this.locale.toUpperCase()] || this.locale.toUpperCase()
    );
  }
}
